import { useState, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  ArrowLeft, ShieldCheck, AlertTriangle, Search, Plus, Minus, Trash2,
  ShoppingBag, BookOpen, X, Layers, Loader2, Heart,
} from 'lucide-react';
import {
  useDeck, useDeckStats, useAddCardToDeck, useUpdateDeckCard,
  useRemoveCardFromDeck, useAddCardByNameToDeck, usePopulateDeck, useClearDeckCards, useBatchAddCards,
} from '@/hooks/useApi';
import { cardService, collectionService, wishlistService } from '@/lib/api';
import toast from 'react-hot-toast';
import { DeckCard, DeckCategory } from '@/types';
import CardImage from '@/components/CardImage';

const CATEGORIES: DeckCategory[] = ['Main Deck', 'Sideboard', 'Extra Deck'];
const GAME_CATEGORY_LABELS: Record<string, DeckCategory[]> = {
  Pokemon: ['Main Deck'],
  Magic:   ['Main Deck', 'Sideboard'],
  YuGiOh: ['Main Deck', 'Extra Deck', 'Sideboard'],
};

// ── Card Preview Panel ─────────────────────────────────────────────────────────

function CardPreview({ card, deckGame }: { card: any | null; deckGame: string }) {
  const navigate = useNavigate();

  if (!card) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-zinc-500 gap-3 p-6">
        <BookOpen className="h-10 w-10" />
        <p className="text-sm text-center">Click a card to preview it here</p>
      </div>
    );
  }

  const name      = card.card_name ?? card.name;
  const setName   = card.set_name ?? card.card?.set_name;
  const type      = card.card_types ?? card.card_type ?? card.card?.card_type;
  const text      = card.card_text ?? card.effects ?? card.card?.card_text ?? card.card?.effects;
  const mana      = card.mana_cost ?? card.card?.mana_cost;
  const power     = card.power ?? card.card?.power;
  const toughness = card.toughness ?? card.card?.toughness;
  const price     = card.market_price ?? card.card?.market_price;

  const handleSearch = () =>
    navigate(`/explore?search=${encodeURIComponent(name)}`);

  return (
    <div className="p-4 flex flex-col gap-3 h-full overflow-y-auto">
      <div className="w-32 mx-auto">
        <div className="aspect-[2.5/3.5] rounded-xl overflow-hidden">
          <CardImage src={undefined} cardName={name} game={deckGame} className="w-full h-full" />
        </div>
      </div>

      <div>
        <h3 className="font-bold text-gray-900 dark:text-zinc-100 text-base leading-tight">{name}</h3>
        {setName && <p className="text-xs text-gray-500 dark:text-zinc-500 mt-0.5">{setName}</p>}
        {type   && <p className="text-xs font-medium text-gray-600 dark:text-zinc-400 mt-1">{type}</p>}
      </div>

      {mana && (
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-zinc-500 font-medium">Mana:</span>
          <span className="text-xs font-mono bg-gray-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">{mana}</span>
        </div>
      )}
      {(power || toughness) && (
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-zinc-500 font-medium">P/T:</span>
          <span className="text-xs font-mono bg-gray-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">{power}/{toughness}</span>
        </div>
      )}
      {text && (
        <p className="text-xs text-gray-600 dark:text-zinc-400 leading-relaxed whitespace-pre-wrap border-t border-gray-200 dark:border-zinc-800 pt-2">{text}</p>
      )}

      <button
        onClick={handleSearch}
        className="mt-1 flex items-center justify-center gap-1.5 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:text-brand-200 font-medium border border-brand-200 rounded-lg py-1.5"
      >
        <Search className="h-3 w-3" /> Find in Explore
      </button>

    </div>
  );
}

// ── Left Panel: Deck Card Row ──────────────────────────────────────────────────

function DeckCardRow({
  card,
  game,
  onSelect,
  selected,
  onIncrement,
  onDecrement,
  onRemove,
  maxCopies,
}: {
  card: DeckCard;
  game: string;
  onSelect: () => void;
  selected: boolean;
  onIncrement: () => void;
  onDecrement: () => void;
  onRemove: () => void;
  maxCopies: number;
}) {
  const owned  = card.owned_quantity ?? 0;
  const needed = card.quantity;
  const hasAll = owned >= needed;
  const name   = card.card_name ?? card.card_id;

  return (
    <div
      onClick={onSelect}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors group
        ${selected ? 'bg-brand-50 dark:bg-brand-900/20 border border-brand-200' : 'hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900'}`}
    >
      <div className="w-8 h-11 rounded overflow-hidden flex-shrink-0">
        <CardImage src={undefined} cardName={name} game={game} className="w-full h-full" compact />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-zinc-100 truncate">{name}</p>
        <p className={`text-xs ${hasAll ? 'text-green-600' : 'text-amber-600'}`}>
          {owned}/{needed} owned
        </p>
      </div>

      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
        <button
          onClick={onDecrement}
          aria-label="Remove one copy"
          className="w-8 h-8 flex items-center justify-center rounded bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 dark:bg-zinc-700 text-gray-600 dark:text-zinc-400"
        >
          <Minus className="h-3 w-3" />
        </button>
        <span className="w-5 text-center text-sm font-bold text-gray-900 dark:text-zinc-100">{needed}</span>
        <button
          onClick={onIncrement}
          disabled={needed >= maxCopies}
          aria-label="Add one copy"
          className="w-8 h-8 flex items-center justify-center rounded bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 dark:bg-zinc-700 text-gray-600 dark:text-zinc-400 disabled:opacity-30"
        >
          <Plus className="h-3 w-3" />
        </button>
        <button
          onClick={onRemove}
          aria-label="Remove card from deck"
          className="w-8 h-8 flex items-center justify-center rounded text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ── Middle Panel: Card Search ──────────────────────────────────────────────────

type SearchTab = 'collection' | 'all' | 'name' | 'load';

function CardSearchPanel({
  game,
  deckId,
  activeCategory,
  deckCardIds,
  onSelectCard,
}: {
  game: string;
  deckId: string;
  activeCategory: DeckCategory;
  deckCardIds: Set<string>;
  onSelectCard: (card: any) => void;
}) {
  const [tab, setTab]             = useState<SearchTab>('collection');
  const [query, setQuery]         = useState('');
  const [nameInput, setNameInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [qty, setQty]             = useState(1);
  const [loadQuery, setLoadQuery]         = useState('');
  const [originalMode, setOriginalMode]   = useState(false);
  const [previewCards, setPreviewCards]   = useState<{ card_id: string; name: string; image_url?: string; quantity: number }[] | null>(null);
  const [previewMeta, setPreviewMeta]     = useState<{ source?: string | null; note?: string | null; suggestion?: string } | null>(null);
  const nameRef                   = useRef<HTMLInputElement>(null);

  const { data: collectionItems = [] } = useQuery(
    ['deckBuilderCollection', game, query],
    () => collectionService.getMyCollection({ game, search: query || undefined, limit: 50 } as any),
    { staleTime: 30_000, keepPreviousData: true }
  );

  const { data: cardResults } = useQuery(
    ['deckBuilderCards', game, query],
    () => cardService.searchCards({ game, search: query, limit: 20, page: 1 }),
    { enabled: tab === 'all' && query.length > 0, staleTime: 60_000 }
  );

  const { data: nameSuggestionsResult, isFetching: fetchingSuggestions } = useQuery(
    ['byNameSuggestions', game, nameInput],
    () => cardService.searchCards({ game, search: nameInput.trim(), limit: 8, page: 1 }),
    { enabled: tab === 'name' && nameInput.trim().length >= 2, staleTime: 60_000, keepPreviousData: false }
  );
  const nameSuggestions: any[] = nameSuggestionsResult?.data ?? [];

  const { mutate: addCard, isLoading: addingById }       = useAddCardToDeck(deckId);
  const { mutate: addByName, isLoading: addingByName }   = useAddCardByNameToDeck(deckId);
  const { mutate: populate,  isLoading: populating }     = usePopulateDeck(deckId);
  const { mutate: batchAdd,  isLoading: committing }     = useBatchAddCards(deckId);

  const collectionCards: any[] = Array.isArray(collectionItems)
    ? collectionItems
    : (collectionItems as any).data ?? [];

  const allCards = cardResults?.data ?? [];
  const isAdding = addingById || addingByName;

  const tabs: { id: SearchTab; label: string }[] = [
    { id: 'collection', label: 'My Collection' },
    { id: 'all',        label: 'All Cards' },
    { id: 'name',       label: 'By Name' },
    { id: 'load',       label: 'Load Deck' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 py-2 text-xs font-medium transition-colors
              ${tab === t.id
                ? 'text-brand-600 dark:text-brand-400 border-b-2 border-brand-600 -mb-px bg-white dark:bg-zinc-900'
                : 'text-gray-500 dark:text-zinc-500 hover:text-gray-700 dark:hover:text-zinc-300 dark:text-zinc-300'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── By Name tab ── */}
      {tab === 'name' && (
        <div className="flex flex-col flex-1 p-4 gap-3">
          <p className="text-xs text-gray-500 dark:text-zinc-500 leading-relaxed">
            Type any card name — we'll look it up from the {game} database and add it to your deck,
            even if you don't own it yet.
          </p>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400 mb-1">Card name</label>
            <div className="relative">
              <input
                ref={nameRef}
                autoFocus
                value={nameInput}
                onChange={e => { setNameInput(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onKeyDown={e => {
                  if (e.key === 'Escape') { setShowSuggestions(false); return; }
                  if (e.key === 'Enter' && nameInput.trim()) {
                    setShowSuggestions(false);
                    addByName(
                      { name: nameInput.trim(), quantity: qty, category: activeCategory },
                      { onSuccess: () => { setNameInput(''); setQty(1); nameRef.current?.focus(); } }
                    );
                  }
                }}
                placeholder={
                  game === 'Pokemon' ? 'e.g. Charizard ex'
                  : game === 'Magic'  ? 'e.g. Lightning Bolt'
                  : 'e.g. Blue-Eyes White Dragon'
                }
                className="w-full text-sm border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-400"
              />

              {/* Live suggestions dropdown */}
              {showSuggestions && nameInput.trim().length >= 2 && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg shadow-lg dark:shadow-black/40 overflow-hidden max-h-64 overflow-y-auto">
                  {fetchingSuggestions ? (
                    <div className="p-3 flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-zinc-500">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Searching…
                    </div>
                  ) : nameSuggestions.length === 0 ? (
                    <div className="p-3 text-xs text-gray-500 dark:text-zinc-500 text-center">
                      No matches — press Enter to add by name anyway
                    </div>
                  ) : (
                    nameSuggestions.map((card: any) => (
                      <button
                        key={card.id}
                        type="button"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          setShowSuggestions(false);
                          setNameInput('');
                          setQty(1);
                          addCard({ card_id: card.id, quantity: qty, category: activeCategory });
                          nameRef.current?.focus();
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-brand-50 dark:hover:bg-brand-900/20 dark:bg-brand-900/20 text-left transition-colors border-b border-gray-50 last:border-0"
                      >
                        <div className="w-6 h-9 rounded overflow-hidden flex-shrink-0">
                          <CardImage src={undefined} cardName={card.name} game={card.game ?? game} className="w-full h-full" compact />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-zinc-100 truncate">{card.name}</p>
                          {card.set_name && (
                            <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">{card.set_name}</p>
                          )}
                        </div>
                        {card.rarity && (
                          <span className="text-xs text-gray-500 dark:text-zinc-500 flex-shrink-0 capitalize">{card.rarity}</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400 mb-1">Quantity</label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setQty(q => Math.max(1, q - 1))}
                className="w-8 h-8 rounded-lg border border-gray-200 dark:border-zinc-800 flex items-center justify-center hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <input
                type="number"
                min={1}
                max={60}
                value={qty}
                onChange={e => setQty(Math.max(1, Math.min(60, Number(e.target.value))))}
                className="w-14 text-center text-sm border border-gray-200 dark:border-zinc-800 rounded-lg py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
              <button
                onClick={() => setQty(q => Math.min(60, q + 1))}
                className="w-8 h-8 rounded-lg border border-gray-200 dark:border-zinc-800 flex items-center justify-center hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <button
            disabled={!nameInput.trim() || isAdding}
            onClick={() => {
              setShowSuggestions(false);
              addByName(
                { name: nameInput.trim(), quantity: qty, category: activeCategory },
                { onSuccess: () => { setNameInput(''); setQty(1); nameRef.current?.focus(); } }
              );
            }}
            className="btn-primary py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isAdding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add to {activeCategory}
          </button>

          <p className="text-xs text-gray-500 dark:text-zinc-500 text-center">Press Enter or click Add</p>

          <div className="mt-auto pt-3 border-t border-gray-200 dark:border-zinc-800">
            <p className="text-xs text-gray-500 dark:text-zinc-500 text-center leading-relaxed">
              Cards added by name will show a placeholder until you find and add them to your collection,
              or click the card to search for it in Explore.
            </p>
          </div>
        </div>
      )}

      {/* ── Load Deck tab ── */}
      {tab === 'load' && (
        <div className="flex flex-col flex-1 p-4 gap-3 overflow-y-auto">
          {/* Original deck toggle */}
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={originalMode}
              onChange={e => {
                setOriginalMode(e.target.checked);
                setPreviewCards(null);
                setPreviewMeta(null);
                setLoadQuery('');
              }}
              className="w-4 h-4 rounded border-gray-300 dark:border-zinc-700 text-brand-600 dark:text-brand-400 focus:ring-brand-400"
            />
            <span className="text-xs font-medium text-gray-700 dark:text-zinc-300">I'm building an original deck</span>
          </label>

          {originalMode ? (
            <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-100 rounded-xl p-4 text-xs text-brand-700 dark:text-brand-300 space-y-2">
              <p className="font-semibold">Original deck mode</p>
              <p className="leading-relaxed text-brand-600 dark:text-brand-400">
                Deck search is disabled. Use the <strong>By Name</strong> tab to add individual cards,
                or browse <strong>My Collection</strong> to add cards you already own.
              </p>
              <button onClick={() => setTab('name')} className="mt-1 text-brand-700 dark:text-brand-300 underline underline-offset-2 font-medium">
                Switch to By Name →
              </button>
            </div>
          ) : (
            <>
              <div>
                <p className="text-xs font-semibold text-gray-700 dark:text-zinc-300 mb-1">Search by deck archetype or card set</p>
                <p className="text-xs text-gray-500 dark:text-zinc-500 leading-relaxed">
                  Search for a deck — review the cards, then click <strong>Set</strong> to add them.
                </p>
              </div>

              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 dark:text-zinc-500" />
                <input
                  value={loadQuery}
                  onChange={e => setLoadQuery(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && loadQuery.trim() && !populating) {
                      setPreviewCards(null);
                      setPreviewMeta(null);
                      populate(
                        { query: loadQuery.trim(), category: activeCategory, preview: true },
                        { onSuccess: (d: any) => { setPreviewCards(d.added ?? []); setPreviewMeta({ source: d.source, note: d.note, suggestion: d.suggestion }); } }
                      );
                    }
                  }}
                  placeholder={
                    game === 'Pokemon' ? 'e.g. Charizard ex, Obsidian Flames…'
                    : game === 'Magic'  ? 'e.g. Lightning Bolt, Goblin Aggro…'
                    : 'e.g. Eldlich, Blue-Eyes, Dark Magician…'
                  }
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 dark:border-zinc-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-400"
                />
              </div>

              <button
                disabled={!loadQuery.trim() || populating}
                onClick={() => {
                  setPreviewCards(null);
                  setPreviewMeta(null);
                  populate(
                    { query: loadQuery.trim(), category: activeCategory, preview: true },
                    { onSuccess: (d: any) => { setPreviewCards(d.added ?? []); setPreviewMeta({ source: d.source, note: d.note, suggestion: d.suggestion }); } }
                  );
                }}
                className="btn-primary py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {populating
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Searching…</>
                  : <><Search className="h-4 w-4" /> Load Cards</>
                }
              </button>

              {/* Preview results */}
              {previewCards !== null && (
                <div className="space-y-2">
                  {previewMeta?.suggestion ? (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300 space-y-2">
                      <p>{previewMeta.suggestion}</p>
                      <button onClick={() => { setOriginalMode(true); setPreviewCards(null); }} className="font-medium underline underline-offset-2">
                        Switch to original deck mode →
                      </button>
                    </div>
                  ) : (
                    <>
                      {previewMeta?.note && (
                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 rounded-lg p-2.5 text-xs text-amber-700 dark:text-amber-300">
                          {previewMeta.note}
                        </div>
                      )}

                      {/* Card preview list */}
                      <div className="border border-gray-200 dark:border-zinc-800 rounded-lg overflow-hidden">
                        <div className="bg-white dark:bg-zinc-900 px-3 py-2 flex items-center justify-between border-b border-gray-200 dark:border-zinc-800">
                          <span className="text-xs font-semibold text-gray-600 dark:text-zinc-400">
                            {previewCards.length} card{previewCards.length !== 1 ? 's' : ''} found
                          </span>
                          <span className="text-xs text-gray-500 dark:text-zinc-500">{activeCategory}</span>
                        </div>
                        <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
                          {previewCards.map(c => (
                            <div key={c.card_id} className="flex items-center gap-2.5 px-3 py-2 text-xs text-gray-700 dark:text-zinc-300">
                              <div className="w-8 h-11 rounded overflow-hidden flex-shrink-0">
                                <CardImage src={undefined} cardName={c.name} className="w-full h-full" compact />
                              </div>
                              <span className="flex-1 truncate font-medium">{c.name}</span>
                              {c.quantity > 1 && (
                                <span className="text-gray-500 dark:text-zinc-500 font-mono">×{c.quantity}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Set / Discard actions */}
                      <div className="flex gap-2">
                        <button
                          disabled={committing || previewCards.length === 0}
                          onClick={() => {
                            batchAdd(
                              previewCards.map(c => ({ card_id: c.card_id, quantity: c.quantity, category: activeCategory })),
                              { onSuccess: () => { setPreviewCards(null); setPreviewMeta(null); setLoadQuery(''); } }
                            );
                          }}
                          className="flex-1 btn-primary py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {committing
                            ? <><Loader2 className="h-4 w-4 animate-spin" /> Adding…</>
                            : <>Set {previewCards.length} card{previewCards.length !== 1 ? 's' : ''}</>
                          }
                        </button>
                        <button
                          onClick={() => { setPreviewCards(null); setPreviewMeta(null); }}
                          className="btn-outline py-2 px-4 text-sm text-gray-500 dark:text-zinc-500"
                        >
                          Discard
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Collection / All Cards tabs ── */}
      {tab !== 'name' && tab !== 'load' && (
        <>
          <div className="p-3 border-b border-gray-200 dark:border-zinc-800">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 dark:text-zinc-500" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={tab === 'collection' ? 'Search your collection…' : 'Search all cards…'}
                className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 dark:border-zinc-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
              {query && (
                <button onClick={() => setQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {(() => {
              const items = tab === 'collection' ? collectionCards : allCards;

              if (tab === 'all' && query.length === 0) {
                return (
                  <div className="p-6 text-center text-sm text-gray-500 dark:text-zinc-500">
                    Type a card name to search the database
                    <br />
                    <button
                      onClick={() => setTab('name')}
                      className="mt-3 text-brand-600 dark:text-brand-400 hover:underline text-xs"
                    >
                      Or add by name directly →
                    </button>
                  </div>
                );
              }

              if (items.length === 0) {
                return (
                  <div className="p-6 text-center text-sm text-gray-500 dark:text-zinc-500">
                    {tab === 'collection'
                      ? query ? 'No matching cards in your collection' : `No ${game} cards in your collection`
                      : 'No cards found'}
                    <br />
                    <button
                      onClick={() => setTab('name')}
                      className="mt-3 text-brand-600 dark:text-brand-400 hover:underline text-xs"
                    >
                      Add by name instead →
                    </button>
                  </div>
                );
              }

              return (
                <div className="divide-y divide-gray-50">
                  {items.map((item: any) => {
                    const cardId = tab === 'collection' ? item.card_id : item.id;
                    const name   = tab === 'collection' ? (item.card?.name ?? item.card_id) : item.name;
                    const price  = tab === 'collection' ? item.card?.market_price : item.market_price;
                    const inDeck = deckCardIds.has(cardId);

                    return (
                      <div
                        key={`${tab}-${cardId}`}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 cursor-pointer"
                        onClick={() => onSelectCard(item)}
                      >
                        <div className="w-8 h-11 rounded overflow-hidden flex-shrink-0">
                          <CardImage src={undefined} cardName={name} game={game} className="w-full h-full" compact />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-zinc-100 truncate">{name}</p>
                          {price != null && (
                            <p className="text-xs text-gray-500 dark:text-zinc-500">${Number(price).toFixed(2)}</p>
                          )}
                        </div>
                        <button
                          disabled={isAdding}
                          onClick={e => {
                            e.stopPropagation();
                            addCard({ card_id: cardId, quantity: 1, category: activeCategory });
                          }}
                          className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors
                            ${inDeck
                              ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:bg-brand-900/40'
                              : 'bg-brand-600 text-white hover:bg-brand-700'}`}
                        >
                          <Plus className="h-3 w-3" />
                          {inDeck ? '+1' : 'Add'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function DeckBuilderPage() {
  const { deckId } = useParams<{ deckId: string }>();
  const navigate   = useNavigate();

  const [activeCategory, setActiveCategory] = useState<DeckCategory>('Main Deck');
  const [selectedCard, setSelectedCard]     = useState<any>(null);

  const { data: deck, isLoading } = useDeck(deckId!);
  const { data: stats }           = useDeckStats(deckId!);

  const { mutate: updateCard } = useUpdateDeckCard(deckId!);
  const { mutate: removeCard } = useRemoveCardFromDeck(deckId!);
  const { mutate: clearCards, isLoading: clearing } = useClearDeckCards(deckId!);

  const [isPushing, setIsPushing] = useState(false);

  const pushToWishlist = async () => {
    if (!deck || isPushing) return;
    const missing = (deck.deck_cards ?? []).filter(c => (c.owned_quantity ?? 0) < c.quantity);
    if (missing.length === 0) return;
    setIsPushing(true);
    let added = 0;
    for (const card of missing) {
      try {
        await wishlistService.quickAdd({
          card_name:     card.card_name ?? '',
          card_image_url: card.card_image_url,
          set_name:      card.set_name,
        });
        added++;
      } catch { /* skip duplicates / failures silently */ }
    }
    setIsPushing(false);
    toast.success(`${added} missing card${added !== 1 ? 's' : ''} added to your Wishlist`);
  };

  const deckCards: DeckCard[] = deck?.deck_cards ?? [];

  const categorized = useMemo(() => {
    const grouped: Record<DeckCategory, DeckCard[]> = {
      'Main Deck': [],
      'Sideboard': [],
      'Extra Deck': [],
    };
    for (const c of deckCards) grouped[c.category]?.push(c);
    return grouped;
  }, [deckCards]);

  const deckCardIds = useMemo(() => new Set(deckCards.map(c => c.card_id)), [deckCards]);

  const validCategories = deck?.game
    ? (GAME_CATEGORY_LABELS[deck.game] ?? CATEGORIES)
    : CATEGORIES;

  const maxCopies = (card: DeckCard) => card.is_basic ? 9999 : 4;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-zinc-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600 dark:text-brand-400" />
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="min-h-screen bg-white dark:bg-zinc-900 flex flex-col items-center justify-center gap-4">
        <p className="text-gray-500 dark:text-zinc-500">Deck not found.</p>
        <Link to="/collection" className="btn-primary px-4 py-2 text-sm">Back to Collection</Link>
      </div>
    );
  }

  const totalMain   = categorized['Main Deck'].reduce((s, c) => s + c.quantity, 0);
  const validStatus = stats?.is_valid ?? deck.is_valid;
  const completion  = stats?.completion_pct ?? 0;
  const missingCount = (stats?.needed_total ?? 0) > 0
    ? (stats!.needed_total - stats!.owned_total)
    : 0;

  return (
    <div className="flex flex-col h-screen bg-white dark:bg-zinc-900 overflow-hidden">
      {/* Top bar */}
      <div className="bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800 px-4 py-3 flex items-center gap-4 flex-shrink-0">
        <button onClick={() => navigate('/collection')} className="text-gray-500 dark:text-zinc-500 hover:text-gray-700 dark:hover:text-zinc-300 dark:text-zinc-300">
          <ArrowLeft className="h-5 w-5" />
        </button>

        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-gray-900 dark:text-zinc-100 text-lg leading-tight truncate">{deck.name}</h1>
          <p className="text-xs text-gray-500 dark:text-zinc-500">{deck.game}{deck.format ? ` · ${deck.format}` : ''}</p>
        </div>

        <div className="hidden sm:flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5">
            <Layers className="h-4 w-4 text-gray-500 dark:text-zinc-500" />
            <span className="font-medium">{totalMain}</span>
            <span className="text-gray-500 dark:text-zinc-500">cards</span>
          </div>
          <div className="flex items-center gap-1.5">
            {validStatus
              ? <ShieldCheck className="h-4 w-4 text-green-500" aria-label="Valid" />
              : <AlertTriangle className="h-4 w-4 text-amber-500" aria-label="Invalid" />
            }
            <span className={`text-xs font-medium ${validStatus ? 'text-green-600' : 'text-amber-600'}`}>
              {validStatus ? 'Valid' : 'Invalid'}
            </span>
          </div>
          <div className="text-xs text-gray-500 dark:text-zinc-500">
            <span className="font-medium text-gray-800 dark:text-zinc-200">{Math.round(completion)}%</span> owned
          </div>
          {missingCount > 0 && (
            <Link
              to={`/collection/decks/${deckId}/missing`}
              className="flex items-center gap-1.5 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:text-brand-200 font-medium"
            >
              <ShoppingBag className="h-3.5 w-3.5" />
              {missingCount} missing
            </Link>
          )}
        </div>

        <Link
          to={`/collection/decks/${deckId}/missing`}
          className="hidden sm:inline-flex btn-outline py-1.5 text-xs items-center gap-1.5"
        >
          <ShoppingBag className="h-3.5 w-3.5" /> Missing Cards
        </Link>

        <button
          disabled={isPushing || missingCount === 0}
          onClick={pushToWishlist}
          title="Add missing cards to your Wishlist so you get notified when they're listed"
          className="hidden sm:inline-flex btn-outline py-1.5 text-xs items-center gap-1.5 text-violet-600 border-violet-200 hover:bg-violet-50 dark:hover:bg-violet-900/20 dark:bg-violet-900/20 disabled:opacity-40"
        >
          {isPushing
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Heart className="h-3.5 w-3.5" />
          }
          Push to Wishlist
        </button>

        <button
          disabled={clearing || totalMain === 0}
          onClick={() => {
            if (window.confirm('Remove all cards from this deck?')) clearCards();
          }}
          className="hidden sm:inline-flex btn-outline py-1.5 text-xs items-center gap-1.5 text-red-500 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" /> Clear All
        </button>
      </div>

      {/* 3-panel body */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left: deck list ── */}
        <div className="w-72 flex flex-col border-r border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0 overflow-hidden">
          <div className="flex border-b border-gray-200 dark:border-zinc-800">
            {validCategories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-1 py-2 text-xs font-medium truncate transition-colors
                  ${activeCategory === cat
                    ? 'text-brand-600 dark:text-brand-400 border-b-2 border-brand-600 -mb-px bg-white dark:bg-zinc-900'
                    : 'text-gray-500 dark:text-zinc-500 hover:text-gray-700 dark:hover:text-zinc-300 dark:text-zinc-300'}`}
              >
                {cat.replace(' Deck', '')}
                <span className="ml-1 text-gray-500 dark:text-zinc-500">
                  ({categorized[cat].reduce((s, c) => s + c.quantity, 0)})
                </span>
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {categorized[activeCategory].length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400 dark:text-zinc-500">
                No cards yet — search or type a name to add
              </div>
            ) : (
              categorized[activeCategory].map(card => (
                <DeckCardRow
                  key={card.id}
                  card={card}
                  game={deck.game}
                  selected={selectedCard?.id === card.id || selectedCard?.card_id === card.card_id}
                  onSelect={() => setSelectedCard(card)}
                  maxCopies={maxCopies(card)}
                  onIncrement={() => updateCard({ cardId: card.card_id, quantity: card.quantity + 1, category: card.category })}
                  onDecrement={() => {
                    if (card.quantity <= 1) {
                      removeCard({ cardId: card.card_id, category: card.category });
                    } else {
                      updateCard({ cardId: card.card_id, quantity: card.quantity - 1, category: card.category });
                    }
                  }}
                  onRemove={() => removeCard({ cardId: card.card_id, category: card.category })}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Middle: card search ── */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900 border-r border-gray-200 dark:border-zinc-800">
          <CardSearchPanel
            game={deck.game}
            deckId={deckId!}
            activeCategory={activeCategory}
            deckCardIds={deckCardIds}
            onSelectCard={card => setSelectedCard(card)}
          />
        </div>

        {/* ── Right: card preview ── */}
        <div className="w-64 xl:w-72 flex-shrink-0 bg-white dark:bg-zinc-900 overflow-hidden flex flex-col">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-zinc-800">
            <p className="text-xs font-semibold text-gray-500 dark:text-zinc-500 uppercase tracking-wide">Preview</p>
          </div>
          <CardPreview card={selectedCard} deckGame={deck.game} />
        </div>
      </div>

      {missingCount > 0 && (
        <div className="sm:hidden bg-amber-50 dark:bg-amber-900/20 border-t border-amber-100 px-4 py-2 flex items-center justify-between">
          <span className="text-xs text-amber-700 dark:text-amber-300">{missingCount} cards missing from your collection</span>
          <Link to={`/collection/decks/${deckId}/missing`} className="text-xs font-medium text-amber-800 dark:text-amber-200 underline">
            View →
          </Link>
        </div>
      )}
    </div>
  );
}
